import {
  getDatabaseErrorDetails,
  sanitizeUrl,
  scrubAttributes,
  scrubFeedbackEvent,
  scrubLog,
  scrubRecordingEvent,
  scrubReplayEvent,
  scrubSpan,
  scrubText,
  scrubTransaction
} from "./chunk-FQGVY4KD.js";

// src/sentry-options.ts
var DEFAULT_TRACES_SAMPLE_RATE = 0.1;
var ALLOWED_REQUEST_HEADERS = ["user-agent"];
var ALLOWED_USER_FIELDS = ["id"];
var BREADCRUMB_URL_KEYS = ["url", "from", "to"];
var IGNORED_ERRORS = [
  // Vidstack's YouTube and Vimeo providers reject pending play/pause requests
  // with this bare string when a player unmounts (e.g. closing a video modal
  // mid-load), and Vidstack rethrows it without a handler. Still present in
  // @vidstack/react 1.15.7.
  /^Non-Error promise rejection captured with value: provider destroyed$/
];
var REPLAY_OPTIONS = {
  maskAllText: true,
  maskAllInputs: true,
  blockAllMedia: true,
  networkDetailAllowUrls: [],
  networkCaptureBodies: false,
  beforeAddRecordingEvent: scrubRecordingEvent
};
var FEEDBACK_OPTIONS = {
  autoInject: false,
  showName: false,
  showEmail: false,
  isNameRequired: false,
  isEmailRequired: false,
  enableScreenshot: false,
  colorScheme: "light",
  formTitle: "Report a problem",
  messageLabel: "What were you doing when this happened?",
  messagePlaceholder: "Please don't include personal details such as phone numbers.",
  submitButtonLabel: "Send report",
  successMessageText: "Thanks, your report has been sent to our team."
};
function getSentryOptions(settings) {
  const dsn = cleanValue(settings.dsn);
  return {
    dsn,
    enabled: Boolean(dsn),
    environment: cleanValue(settings.environment) ?? "development",
    tracesSampleRate: parseSampleRate(settings.tracesSampleRate),
    ignoreErrors: IGNORED_ERRORS,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [
      {
        name: "PrivacyScrubbing",
        setup(client) {
          client.on("createDsc", (context) => {
            if (context.transaction) {
              context.transaction = scrubText(context.transaction);
            }
          });
          client.addEventProcessor((event) => {
            if (event.type !== "feedback") {
              return event;
            }
            return scrubFeedbackEvent(scrubSentryEvent(event));
          });
        }
      }
    ],
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: {
        request: { allow: ALLOWED_REQUEST_HEADERS },
        response: false
      },
      httpBodies: [],
      urlQueryParams: false,
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      stackFrameVariables: false
    },
    beforeSend: scrubSentryEvent,
    beforeSendLog: scrubLog,
    beforeSendSpan: scrubSpan
  };
}
function scrubSentryEvent(event, hint) {
  const databaseError = getDatabaseErrorDetails(hint?.originalException) ?? getDatabaseErrorDetails(event.exception);
  if (databaseError) {
    for (const exception of event.exception?.values ?? []) {
      exception.value = databaseError.message;
      delete exception.stacktrace;
    }
    if (databaseError.code) {
      event.tags = { ...event.tags, "db.sqlstate": databaseError.code };
    }
  }
  if (event.request) {
    delete event.request.cookies;
    delete event.request.data;
    delete event.request.query_string;
    if (event.request.headers) {
      event.request.headers = Object.fromEntries(
        Object.entries(event.request.headers).filter(
          ([header]) => ALLOWED_REQUEST_HEADERS.includes(header.toLowerCase())
        )
      );
    }
    if (event.request.url) {
      const url = sanitizeUrl(event.request.url);
      if (url) {
        event.request.url = url;
      } else {
        delete event.request.url;
      }
    }
  }
  const nextjs = event.contexts?.nextjs;
  if (typeof nextjs?.request_path === "string") {
    const path = sanitizeUrl(nextjs.request_path);
    if (path) {
      nextjs.request_path = path;
    } else {
      delete nextjs.request_path;
    }
  }
  if (event.user) {
    event.user = Object.fromEntries(
      ALLOWED_USER_FIELDS.filter((field) => event.user?.[field] !== void 0).map((field) => [
        field,
        event.user?.[field]
      ])
    );
  }
  if (event.message) {
    event.message = scrubText(event.message);
  }
  for (const exception of event.exception?.values ?? []) {
    if (exception.value) {
      exception.value = scrubText(exception.value);
    }
  }
  if (event.extra) {
    event.extra = scrubAttributes(event.extra);
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.filter((breadcrumb) => breadcrumb.category !== "console");
  }
  for (const breadcrumb of event.breadcrumbs ?? []) {
    if (breadcrumb.message) {
      breadcrumb.message = scrubText(breadcrumb.message);
    }
    if (!breadcrumb.data) {
      continue;
    }
    const urls = {};
    for (const key of BREADCRUMB_URL_KEYS) {
      const value = breadcrumb.data[key];
      if (typeof value === "string") {
        urls[key] = sanitizeUrl(value) ?? "[redacted]";
      }
    }
    breadcrumb.data = { ...scrubAttributes(breadcrumb.data), ...urls };
  }
  return event;
}
function parseSampleRate(value) {
  const rate = Number(cleanValue(value));
  if (Number.isFinite(rate) && rate >= 0 && rate <= 1 && cleanValue(value) !== void 0) {
    return rate;
  }
  return DEFAULT_TRACES_SAMPLE_RATE;
}
function cleanValue(value) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : void 0;
}
export {
  FEEDBACK_OPTIONS,
  REPLAY_OPTIONS,
  getSentryOptions,
  sanitizeUrl,
  scrubAttributes,
  scrubFeedbackEvent,
  scrubLog,
  scrubRecordingEvent,
  scrubReplayEvent,
  scrubSentryEvent,
  scrubSpan,
  scrubText,
  scrubTransaction
};
